<template>
  <div>
    <h2 class="text-3xl mb-2" data-testid="central-costs-heading">
      {{ $t('central-costs.heading') }}
    </h2>
    <fetch-data
      v-slot="{ isLoading }"
      url="central-costs"
      :params="computedQuery"
      :debounce="FILTER_DEBOUNCE"
      api-v2
      @data-deliver="updateList"
    >
      <div class="container">
        <div v-if="isLoading" class="spinner-area">
          <icon-spinner
            class="spinner stroke-primary"
            width="64px"
            height="64px"
          />
        </div>
        <div
          v-if="centralCostsList.length"
          class="flex flex-wrap"
          :class="{ 'opacity-50': isLoading }"
        >
          <card
            v-for="centralCost in centralCostsList"
            :key="centralCost.id"
            :image="centralCost.logoUrl"
            hide-size-in-src
            :data-testid="'central-cost-card-' + centralCost.id"
            :alt="
              $t('central-costs.card-alt', {
                name: centralCost.name,
              })
            "
            class="mt-4 mr-4"
            cover-img
            :link="{
              name: 'Objects',
              query: { centralCostId: centralCost.id },
            }"
          >
            {{ centralCost.name }}
          </card>
        </div>
        <div v-else-if="!isLoading" class="text-center py-8 text-gray-500">
          {{ $t('central-costs.no-data') }}
        </div>
        <div class="flex w-full my-3 justify-center">
          <pagination
            v-if="centralCostsList.length"
            :page-count="lastPage"
            :current-page="currentPage"
            class="mt-2"
            @change-page="changePage"
          />
        </div>
      </div>
    </fetch-data>
  </div>
</template>

<script>
import { defineComponent, computed, ref } from '@vue/composition-api';
import Card from '@/components/Card/Card.vue';
import FetchData from '@/components/DataProvider/FetchData.vue';
import IconSpinner from '@/components/Icon/icons/IconSpinner.vue';
import Pagination from '@/components/Pagination/Pagination.vue';
import { useRouteQuery } from '@/composables';
import { FILTER_DEBOUNCE } from '@/constants/filters';
import { mapQueryToApiV2 } from '@/util/query';

export default defineComponent({
  name: 'CentralCosts',
  components: {
    Card,
    FetchData,
    IconSpinner,
    Pagination,
  },
  setup(props, ctx) {
    const centralCostsList = ref([]);
    const lastPage = ref();
    const { routeQuery, changePage } = useRouteQuery(ctx);

    const computedQuery = computed(() => {
      const { q, ...query } = mapQueryToApiV2(routeQuery.value);
      return {
        ...query,
        name: q,
        brandId: ctx.root.$route.params.id,
      };
    });

    const currentPage = computed(() => {
      return computedQuery.value?.page ?? 1;
    });

    const updateList = (data) => {
      centralCostsList.value = data.nodes;
      lastPage.value = Math.ceil(
        data.totalCount / (computedQuery.value?.perPage || 8),
      );
    };

    return {
      FILTER_DEBOUNCE,
      centralCostsList,
      lastPage,
      currentPage,
      computedQuery,
      updateList,
      changePage,
    };
  },
});
</script>

<style lang="scss" scoped>
.container {
  @apply flex flex-wrap relative;
}

.spinner-area {
  @apply py-48 w-full flex justify-center absolute z-50;
}
</style>
